import React, { useState, useEffect } from 'react';
import { Row, Col, Tabs, Spin, Alert, Typography } from 'antd';
import FreeAgentsTable from './Components/FreeAgentsTable';
import RosterTable from './Components/RosterTable';
import LeagueCard from './Components/LeagueCard';
import LeagueDropdown from './Components/LeagueDropdown';
import FilterDropdown from './Components/FilterDropdown';
import IRPanel from './Components/IRPanel';

const { Text } = Typography;

// Single-league-at-a-time waivers workflow: everything (roster, free agents,
// trends, open slots, FAAB) is pulled live from MFL when a league is selected
// or refreshed - no stale DB snapshots are shown in the UI.
const WaiverClaims = () => {
  const DATA_YEAR = 2026;
  const DATA_WEEK = 1;

  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueID, setSelectedLeagueID] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [loadingLeague, setLoadingLeague] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [selectedAdds, setSelectedAdds] = useState([]);
  const [selectedDrops, setSelectedDrops] = useState([]);
  const [filterPosition, setFilterPosition] = useState(null);
  const [filterTeam, setFilterTeam] = useState(null);
  const [filterDepthChart, setFilterDepthChart] = useState(null);

  useEffect(() => {
    fetch('/api/waivers/myLeagues?userid=1')
      .then((res) => res.json())
      .then((data) => setLeagues(data.response || []))
      .catch((err) => console.error('Failed to load leagues', err));
  }, []);

  const refreshLeagueLive = async (leagueID, resetSelections = true) => {
    setLoadingLeague(true);
    setLoadError(null);
    try {
      const response = await fetch('/api/waivers/liveLeagueData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userid: 1,
          leagueID,
          datayear: DATA_YEAR,
          dataweek: DATA_WEEK
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to load live league data');

      setLiveData(data.response);

      if (resetSelections) {
        setSelectedAdds([]);
        setSelectedDrops([]);
      }

      return data;
    } catch (err) {
      setLoadError(err.message || 'Failed to load live league data');
      throw err;
    } finally {
      setLoadingLeague(false);
    }
  };

  const handleSelectLeague = (leagueID) => {
    setSelectedLeagueID(leagueID);
    setLiveData(null);
    if (leagueID) refreshLeagueLive(leagueID, true);
  };

  const freeAgents = liveData?.freeAgents || [];
  const rosterPlayers = liveData?.rosterPlayers || [];

  const handleSelectAdd = (playerID) => {
    const player = freeAgents.find((p) => String(p.playerID) === String(playerID));
    if (!player) return;
    setLiveData((prev) => ({ ...prev, freeAgents: prev.freeAgents.filter((p) => String(p.playerID) !== String(playerID)) }));
    setSelectedAdds((prev) => [...prev, { ...player, bidAmount: 0 }]);
  };

  const handleSelectDrop = (playerID) => {
    const player = rosterPlayers.find((p) => String(p.playerID) === String(playerID));
    if (!player) return;
    setLiveData((prev) => ({ ...prev, rosterPlayers: prev.rosterPlayers.filter((p) => String(p.playerID) !== String(playerID)) }));
    setSelectedDrops((prev) => [...prev, player]);
  };

  // Moves an item within the ranked list from one index to another
  const reorderList = (setter, fromIndex, toIndex) => {
    setter((prev) => {
      const list = [...prev];
      if (toIndex < 0 || toIndex >= list.length) return prev;
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      return list;
    });
  };

  const handleReorderAdd = (_leagueID, fromIndex, toIndex) => reorderList(setSelectedAdds, fromIndex, toIndex);
  const handleReorderDrop = (_leagueID, fromIndex, toIndex) => reorderList(setSelectedDrops, fromIndex, toIndex);

  const handleBidChange = (_leagueID, index, value) => {
    setSelectedAdds((prev) => {
      const list = [...prev];
      list[index] = { ...list[index], bidAmount: value || 0 };
      return list;
    });
  };

  const handleRemoveAdd = (_leagueID, index) => {
    setSelectedAdds((prev) => {
      const list = [...prev];
      const [removed] = list.splice(index, 1);
      if (removed) setLiveData((ld) => ({ ...ld, freeAgents: [...ld.freeAgents, removed] }));
      return list;
    });
  };

  const handleRemoveDrop = (_leagueID, index) => {
    setSelectedDrops((prev) => {
      const list = [...prev];
      const [removed] = list.splice(index, 1);
      if (removed) setLiveData((ld) => ({ ...ld, rosterPlayers: [...ld.rosterPlayers, removed] }));
      return list;
    });
  };

  const buildClaimPayload = () => ({
    userid: 1,
    leagueID: selectedLeagueID,
    adds: selectedAdds.map((p) => ({ playerID: p.playerID, bidAmount: p.bidAmount || 0 })),
    drops: selectedDrops.map((p) => ({ playerID: p.playerID }))
  });

  const handlePreviewClaims = async () => {
    const response = await fetch('/api/waivers/previewClaims', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildClaimPayload())
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || data.message || 'Failed to preview claims');
    return data;
  };

  const handleSubmitClaims = async () => {
    const response = await fetch('/api/waivers/submitClaims', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildClaimPayload())
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || data.message || 'Failed to submit claims');
    await refreshLeagueLive(selectedLeagueID, true);
    return data;
  };

  const handlePreviewIR = async () => {
    const response = await fetch('/api/waivers/irMovesAll', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userid: 1, datayear: DATA_YEAR })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || data.message || 'Failed to preview IR moves');
    if (data.errors?.length) {
      const errorSummary = data.errors.map((item) => `${item.leagueName || item.leagueID}: ${item.error}`).join(' · ');
      throw new Error(`IR preview checked ${data.leaguesChecked - data.errors.length}/${data.leaguesChecked} leagues. ${errorSummary}`);
    }
    return data.response || [];
  };

  const handleSubmitIR = async (movesByLeague) => {
    const results = await Promise.all(movesByLeague.map(async ({ leagueID, activate, deactivate, drop }) => {
      const response = await fetch('/api/waivers/submitIRMoves', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leagueID, activate, deactivate, drop })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || `Failed to submit IR moves for league ${leagueID}`);
      return { leagueID, ...data };
    }));
    if (selectedLeagueID) await refreshLeagueLive(selectedLeagueID, true);
    return results;
  };

  const uniqueValues = (key) => [...new Set([...freeAgents, ...rosterPlayers].map((p) => p[key]).filter(Boolean))].sort();
  const uniquePositions = uniqueValues('position');
  const uniqueTeams = uniqueValues('team');
  const uniqueDepthCharts = uniqueValues('depthChart');

  const leagueInfo = liveData
    ? { leagueID: liveData.leagueID, leagueName: liveData.leagueName }
    : (selectedLeagueID ? { leagueID: selectedLeagueID, leagueName: leagues.find((l) => String(l.leagueID) === String(selectedLeagueID))?.leagueName } : null);

  const waiversTab = (
    <div>
      <LeagueDropdown setFilterLeague={handleSelectLeague} leagues={leagues} />
      {loadError && <Alert style={{ marginBottom: 16 }} type="error" message={loadError} />}
      {!selectedLeagueID && <Text type="secondary">Select a league above to load its current roster and free agents live from MFL.</Text>}
      {loadingLeague && <Spin style={{ marginLeft: 12 }} />}

      {liveData && (
        <>
          <FilterDropdown placeholder="Filter by Position" options={uniquePositions} value={filterPosition} onChange={setFilterPosition} />
          <FilterDropdown placeholder="Filter by Team" options={uniqueTeams} value={filterTeam} onChange={setFilterTeam} />
          <FilterDropdown placeholder="Filter by Depth Chart" options={uniqueDepthCharts} value={filterDepthChart} onChange={setFilterDepthChart} />

          <Row gutter={16}>
            <Col span={16}>
              <Row gutter={8}>
                <Col span={12}>
                  <FreeAgentsTable
                    freeAgents={freeAgents}
                    onAdd={handleSelectAdd}
                    filterLeague={null}
                    filterPosition={filterPosition}
                    filterTeam={filterTeam}
                    filterDepthChart={filterDepthChart}
                  />
                </Col>
                <Col span={12}>
                  <RosterTable
                    rosterPlayers={rosterPlayers}
                    onDrop={handleSelectDrop}
                    filterLeague={null}
                    filterPosition={filterPosition}
                    filterTeam={filterTeam}
                    filterDepthChart={filterDepthChart}
                  />
                </Col>
              </Row>
            </Col>
            <Col span={8}>
              <LeagueCard
                leagueInfo={leagueInfo}
                openSlots={liveData.openSlots}
                bbidBalance={liveData.bbidBalance}
                selectedAdds={selectedAdds}
                selectedDrops={selectedDrops}
                onReorderAdd={handleReorderAdd}
                onReorderDrop={handleReorderDrop}
                onBidChange={handleBidChange}
                onRemoveAdd={handleRemoveAdd}
                onRemoveDrop={handleRemoveDrop}
                onPreview={handlePreviewClaims}
                onSubmit={handleSubmitClaims}
                onRefreshLive={() => refreshLeagueLive(selectedLeagueID, false)}
                liveLoading={loadingLeague}
              />
            </Col>
          </Row>
        </>
      )}
    </div>
  );

  const irTab = (
    <div>
      <IRPanel leagues={leagues} onPreview={handlePreviewIR} onSubmit={handleSubmitIR} />
    </div>
  );

  return (
    <Tabs
      items={[
        { key: 'waivers', label: 'Waivers', children: waiversTab },
        { key: 'ir', label: 'Injured Reserve', children: irTab }
      ]}
    />
  );
};

export default WaiverClaims;

